import {
    Box,
    Button as MUIButton,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    MenuItem,
    Modal as MUIModal,
    Select,
    Skeleton,
    TablePagination,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Loader } from '@zaplify/frontend-common';
import { Button, Modal } from '@zaplify/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { useNavigate } from 'react-router-dom';
import { previewProspectsAction, purchaseProspects } from '../../../../redux/ducks/prospect-search-result/operations';
import { updatePage } from '../../../../redux/ducks/prospect-search-result/slice';
import { AppDispatch, useAppSelector } from '../../../../redux/store/configureStore';
import { ProspectsPanel } from './prospect-details';
import { MoveProspectsToCampaign } from '../../../../components/moveProspectsToCampaign/MoveProspectsToCampaign';
import { ProspectsTableRow } from './table-row';
import { ProspectDataDto } from '@zaplify/prospects';
import { useProspectCollections } from '../../../../hooks/use-prospect-collections';
import { MoveProspectsToCampaignContainer } from '../../../../components/moveProspectsToCampaign/moveProspectsToCampaign.styled';
import CloseIcon from '../../../../components/icons/close';
import { handleSuccessNotification } from '../../../../redux/actions';
import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { getFormattedProspectSearch } from '../../../../redux/ducks/prospect-search-result/helpers';
import { useMsal } from '@azure/msal-react';

type Props = {
    onModalClose: () => void;
    isOpen: boolean;
};

const BulkAddModal = ({ isOpen, onModalClose }: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { prospectCollections } = useProspectCollections();
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const msal = useMsal();
    const profilesSdk = getZaplifySdk(apiEndpoint, msal.instance).profiles;
    const maxProspects = useAppSelector((state) => state.prospectSearchResult?.hits?.available ?? 0);
    const prospectSearchFilters = useAppSelector((state) => state.prospectSearchFilters);

    const [targetProspectCollectionId, setTargetProspectCollectionId] = useState('');
    const [numProspects, setNumProspects] = useState(1);
    const [loadingProgress, setLoadingProgress] = useState(1);

    const handleClose = () => {
        setTargetProspectCollectionId('');
        onModalClose();
    };

    const handleChange = (event) => {
        setTargetProspectCollectionId(event.target.value);
    };

    const handleAddProspects = async () => {
        setLoading(true);
        let personIds = [];
        let fromCompanyIndex = 0;
        const batchSize = 200;

        while (personIds.length < numProspects) {
            const prospectSearchFilter = getFormattedProspectSearch({
                filters: prospectSearchFilters,
                fromCompanyIndex,
                amountOfCompanies: batchSize,
            });
            const prospectsResponse = await profilesSdk.sources.previewProspectingList(
                prospectSearchFilter,
                false,
                'campaignIDTEST____TEST'
            );
            const newPersonIds = prospectsResponse?.persons.map((person) => person.personId);
            personIds = personIds.concat(newPersonIds);
            fromCompanyIndex += batchSize;

            if (newPersonIds.length < batchSize) {
                // If fewer prospects were returned than requested, we've exhausted the available prospects
                break;
            }
            setLoadingProgress((personIds.length / numProspects) * 40); // Dont set progress to 100, but stop at 50 in order to allow for purchasing
        }

        let intervalId: NodeJS.Timeout;
        if (numProspects > 300) {
            // Update progress bar for purchasing, which should take about 35ms per prospect
            const intervals = [45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100];
            let index = 0;
            intervalId = setInterval(() => {
                if (index < intervals.length) {
                    setLoadingProgress(intervals[index]);
                    index++;
                } else {
                    clearInterval(intervalId);
                }
            }, (personIds.length * 35) / intervals.length);
        }

        // Trim the array to the desired number of prospects
        personIds = personIds.slice(0, numProspects);
        await dispatch(
            purchaseProspects({
                ids: personIds,
                navigate: null,
                resyncHits: false,
                toOtherCampaignId: targetProspectCollectionId,
            })
        );
        intervalId && clearInterval(intervalId);
        setLoading(false);
        setLoadingProgress(0);
        dispatch(
            handleSuccessNotification(
                `Successfully added prospects to ${
                    prospectCollections?.find((pc) => pc.id === targetProspectCollectionId)?.name
                }`,
                10000
            )
        );
        handleClose();
    };

    useEffect(() => {
        setDisabled(targetProspectCollectionId === '' || numProspects < 1 || numProspects > maxProspects);
    }, [targetProspectCollectionId, numProspects]);

    return (
        <>
            <MUIModal open={isOpen} onClose={handleClose} onClick={(event) => event.stopPropagation()}>
                <MoveProspectsToCampaignContainer>
                    <div className="modal-container">
                        <div className="input-container">
                            <div>
                                <IconButton
                                    onClick={handleClose}
                                    disableRipple
                                    className="close-icon"
                                    data-Cy="close-icon"
                                    size="large"
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Typography variant="h3" className="title margin-bottom">
                                    Bulk add to collection
                                </Typography>
                            </div>
                            <div>
                                <Typography className="typography title-small">Select prospect collection</Typography>
                                <FormControl className="form-control" variant="filled">
                                    <Select
                                        value={targetProspectCollectionId}
                                        onChange={handleChange}
                                        displayEmpty
                                        disableUnderline
                                        className={targetProspectCollectionId === '' ? 'disabled' : 'active'}
                                        MenuProps={{ style: { maxWidth: '1px' } }} // kinda hacky, but it works for line MenuItem span
                                        data-cy="select-campaign-dropdown"
                                    >
                                        <MenuItem value="" disabled>
                                            Select...
                                        </MenuItem>
                                        {prospectCollections &&
                                            prospectCollections.map(({ name, id }) => (
                                                <MenuItem key={id} value={id} data-id={id} id={id} className="option">
                                                    <span
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {name}
                                                    </span>
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div>
                                <Typography className="typography title-small">{`Number of prospects (Max ${maxProspects})`}</Typography>
                                <FormControl className="form-control" variant="filled">
                                    <TextField
                                        variant="outlined"
                                        className="input"
                                        onChange={(e) => {
                                            const value = e.target.value === '' ? 0 : Number(e.target.value);
                                            setNumProspects(value);
                                        }}
                                        value={numProspects}
                                        error={numProspects < 1 || numProspects > maxProspects}
                                        sx={{
                                            maxWidth: '80px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                height: '40px',
                                                textAlign: 'center',
                                            },
                                            '& input': {
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                </FormControl>
                            </div>
                            {`The first ${numProspects} prospects in the search result will be added to the collection`}
                        </div>
                        <Divider className="divider" />
                        <div className="buttons-container">
                            <MUIButton
                                className="button button-outlined-blue "
                                variant="outlined"
                                data-cy="close-button"
                                onClick={handleClose}
                            >
                                Close
                            </MUIButton>
                            <Tooltip title={disabled ? 'Please select a collection and number of prospects' : ''}>
                                <Box sx={{ display: 'flex', gap: '16px' }}>
                                    <MUIButton
                                        className={`${disabled ? 'disabled' : 'button-blue'} button`}
                                        disabled={disabled}
                                        variant="contained"
                                        endIcon={
                                            loading && (
                                                <CircularProgress
                                                    color="inherit"
                                                    size={20}
                                                    variant="determinate"
                                                    value={loadingProgress}
                                                />
                                            )
                                        }
                                        onClick={() => handleAddProspects()}
                                        data-cy="move-button"
                                    >
                                        {'Add'}
                                    </MUIButton>
                                </Box>
                            </Tooltip>
                        </div>
                    </div>
                </MoveProspectsToCampaignContainer>
            </MUIModal>
        </>
    );
};

const TableTopSection = ({ selected, handlePurchaseClick }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleBulkAddClick = () => {
        setIsOpen(true);
    };

    const handleCloseModal = () => {
        setIsOpen(false);
    };
    const numSelected = selected?.length;
    const purchasing = useAppSelector((state) => state.prospectSearchResult.purchasing);

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '16px',
                width: '100%',
                justifyContent: 'flex-end',
                position: 'absolute',
                top: 0,
                right: 0,
            }}
        >
            <Button
                height="40px"
                width="140px"
                startIcon={<Plus strokeWidth={1} size={20} />}
                onClick={() => handleBulkAddClick()}
                classes={['mint']}
            >
                Bulk add...
            </Button>
            {!!selected?.length && (
                <Box sx={{ display: 'flex' }}>
                    <Box onClick={(e) => handlePurchaseClick(e, selected)}>
                        <Tooltip title="Add & Unlock selected prospects to another campaign" arrow>
                            <Button
                                classes={['mint']}
                                height="40px"
                                startIcon={purchasing ? <Loader size={20} /> : <Plus strokeWidth={1} size={20} />}
                            >
                                {purchasing ? 'Adding...' : `Add selected to collection (${numSelected})`}
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            <BulkAddModal isOpen={isOpen} onModalClose={handleCloseModal} />
        </Box>
    );
};
export const TableView = ({ rows, totalCount }: { rows: ProspectDataDto[]; totalCount: number }) => {
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [selectedExpandProspect, setSelectedExpandProspect] = React.useState<ProspectDataDto>(null);
    const page = useAppSelector((state) => state.prospectSearchResult.page);
    const loadingProspects = useAppSelector((state) => state.prospectSearchResult.loading);
    const prospectsHaveLoaded = useAppSelector((state) => state.prospectSearchResult.persons?.length > 0);
    const prospectSearchFilters = useAppSelector((state) => state.prospectSearchFilters);
    const purchasedProspects = useAppSelector((state) => state.prospectSearchResult.prospectTransactionState);
    const credits = useAppSelector((state) => state.user.zaplifyUser.credits);
    const availableCredits = credits?.available || 0;
    const totalAvailableCredits = availableCredits < 0 ? 0 : availableCredits;
    const numSelected = selected?.length;

    const initialPage = React.useRef(page);
    const navigate = useNavigate();

    const dispatch = useDispatch<AppDispatch>();

    const [shouldOpenMoveModal, setShouldOpenMoveModal] = useState(false);

    const openMoveToCampaignModal = () => setShouldOpenMoveModal(true);

    const closeMoveToCampaignModal = () => setShouldOpenMoveModal(false);

    const handlePurchaseClick = (e, selected: string[]) => {
        e.stopPropagation();

        if (false) {
            purchaseProspectsToContacts(selected);
            return;
        } else {
            setSelected(selected);
        }
        openMoveToCampaignModal();
    };

    const purchaseProspectsToContacts = async (selectedProspects) => {
        try {
            await dispatch(
                purchaseProspects({
                    ids: selectedProspects,
                    navigate: null,
                    resyncHits: false,
                })
            );
            setSelected([]);
        } catch (error) {
            console.log("Couldn't purchase prospects", error);
        } finally {
        }
    };

    const setPage = (number: number) => {
        dispatch(updatePage(number));
        window.scrollTo(0, 0);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n.personId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
        event.stopPropagation();

        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly string[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage + 1);
    };

    React.useEffect(() => {
        if (initialPage.current === page) return;
        console.log('page changed', page, initialPage.current);
        if (prospectsHaveLoaded) {
            initialPage.current = page;
            console.log('dispatching previewProspectsAction');
            dispatch(
                previewProspectsAction({ prospectSearchFilters, pagination: true, navigate, companiesPerPage: 50 })
            );
        }
    }, [page]);

    if (loadingProspects)
        return (
            <TableContainer component={Paper} sx={{ width: '100%' }}>
                <Skeleton sx={{ width: '100%', height: '60px' }} />
            </TableContainer>
        );

    return (
        <>
            <TableTopSection selected={selected} handlePurchaseClick={handlePurchaseClick} />
            <TableContainer
                component={Paper}
                sx={{
                    width: '100%',
                    boxShadow: 'none',
                    overflowX: 'unset',
                    border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                    borderRadius: 'var(--radius-xl, 14px)',
                    marginBottom: '40px',
                }}
            >
                <Table
                    size="small"
                    sx={{
                        borderRadius: 'var(--radius-xl, 14px)',
                        width: '100%',
                        background: 'var(--grey-palette-grey-1, #F7F8FA)',
                    }}
                    aria-label="simple table"
                >
                    <TableHead
                        sx={{
                            padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                            gap: 'var(--S-s-regular, 8px)',
                            alignSelf: 'stretch',
                            borderRadius:
                                'var(--radius-xl, 14px) var(--radius-xl, 14px) var(--radius-none, 0px) var(--radius-none, 0px)',
                            background: 'var(--grey-palette-grey-1, #F7F8FA)',
                        }}
                    >
                        <TableRow
                            sx={{
                                padding: '4px var(--M-m-compact, 16px) var(--M-m-compact, 16px) 8px',
                                borderRadius: 'var(--radius-xl, 14px)',

                                '& > th:first-child': {
                                    borderRadius: '14px 0 0 0',
                                },
                                '& > th:last-child': {
                                    borderRadius: '0 14px 0 0',
                                },
                                '& th': {
                                    color: '#6B7985',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    padding: '6px 12px',
                                },
                            }}
                        >
                            <TableCell>
                                <Checkbox
                                    onChange={handleSelectAllClick}
                                    color="primary"
                                    indeterminate={selected?.length > 0 && selected?.length < rows.length}
                                    checked={rows.length > 0 && selected?.length === rows.length}
                                />
                            </TableCell>
                            <TableCell sx={{ paddingLeft: '44px !important' }}>Prospect</TableCell>
                            <TableCell sx={{ paddingLeft: '56px !important' }} align="left">
                                Company
                            </TableCell>
                            <TableCell align="left">Industry</TableCell>
                            <TableCell align="left">Size</TableCell>
                            <TableCell align="left" sx={{ width: '19%' }}>
                                Prospect location
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={{
                            boxShadow: '0px 1px 4px 0px rgba(20, 21, 26, 0.02), 0px 2px 7px 0px rgba(20, 21, 26, 0.07)',
                            //border: '1px solid var(--grey-palette-grey-3, #E4E6EB)',
                            borderRadius: 'var(--radius-xl, 14px)',
                            '& > tr:first-child': {
                                '& > td:first-child': {
                                    borderRadius: '14px 0 0 0',
                                },
                                '& > td:last-child': {
                                    borderRadius: '0 14px 0 0',
                                },
                            },
                            '& > tr:last-child': {
                                '& > td:first-child': {
                                    borderRadius: '0 0 0 14px',
                                },
                                '& > td:last-child': {
                                    borderRadius: '0 0 14px 0',
                                },
                            },
                        }}
                    >
                        {rows.map((row) => (
                            <ProspectsTableRow
                                key={row.personId}
                                row={row}
                                handleClick={handleClick}
                                selected={selected}
                                setSelectedExpandProspect={setSelectedExpandProspect}
                                transactionState={purchasedProspects[row.personId]}
                                handlePurchaseClick={handlePurchaseClick}
                            />
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={rows.length < 50 ? (page - 1) * 50 + rows.length : totalCount > 100 ? totalCount : -1}
                    rowsPerPage={50}
                    labelRowsPerPage={null}
                    page={page - 1}
                    onPageChange={handleChangePage}
                    showFirstButton
                    sx={{
                        background: 'var(--grey-palette-grey-1, #F7F8FA)',
                        borderRadius: '0 0 14px 14px',
                        '& .MuiTablePagination-select': {
                            display: 'none',
                            '& svg': {
                                display: 'none',
                            },
                        },
                        '& .MuiInputBase-root svg': {
                            display: 'none',
                        },
                    }}
                />
            </TableContainer>
            {selectedExpandProspect && (
                <Box
                    sx={{
                        '& .ReactModal__Content': {
                            inset: '0 important!',
                            background: 'grey',
                        },
                    }}
                >
                    <Modal
                        isOpen
                        style={{
                            inset: '0px',
                            content: {
                                right: '4px',
                                top: '4px',
                                inset: 'none',
                                background: 'none',
                                transform: 'none',
                                height: '98vh',
                                width: '308px',
                                marginLeft: 'auto',
                                borderRadius: '14px',
                            },
                        }}
                        closeModal={() => setSelectedExpandProspect(null)}
                        disableScrolling
                        zIndex="9999"
                    >
                        <ProspectsPanel
                            onClose={() => setSelectedExpandProspect(null)}
                            prospect={selectedExpandProspect}
                            isPreview
                            prospectIsLoading={false}
                        />
                    </Modal>
                </Box>
            )}
            {shouldOpenMoveModal && (
                <MoveProspectsToCampaign
                    moveAndPurchase
                    shouldShowNumbers
                    onMoveProspects={() => setSelected([])}
                    selectedProspects={selected}
                    onModalClose={closeMoveToCampaignModal}
                    text={
                        numSelected > 1
                            ? `${numSelected} Prospects added to another campaign`
                            : `1 Prospect added to another campaign`
                    }
                />
            )}
        </>
    );
};
